import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import notification from "antd/es/notification";
import Paragraph from "antd/es/typography/Paragraph";
import cn from "classnames";
import { useIntl } from "react-intl";

import Draft from "@mapmycustomers/shared/types/draft/Draft";
import EmailDraft from "@mapmycustomers/shared/types/draft/EmailDraft";
import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import { EntityTypesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { Divider, Dropdown, Modal } from "@mapmycustomers/ui";

import DraftApplyButton from "@app/component/email/creation/EmailDraftsModal/DraftApplyButton";
import DraftDeleteUndoButton from "@app/component/email/creation/EmailDraftsModal/DraftDeleteUndoButton";
import EmailSubjectView from "@app/component/email/creation/EmailSubjectView";
import MoreButton from "@app/component/MoreButton";
import { getEmailDraftsByEntityType } from "@app/store/email";
import { createEmailDraft, deleteEmailDraft } from "@app/store/email/actions";
import { RootState } from "@app/store/rootReducer";
import EmailEntityOpenCallback from "@app/types/email/EmailEntityOpenCallback";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useEmailDraftInfoGetter from "@app/util/email/useEmailDraftInfoGetter";
import { formatDate } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";

import DraftView from "./DraftView";
import styles from "./EmailDraftsModal.module.scss";
import messages from "./messages";

interface EmailDraftsModalProps {
  entityType: EntityTypesSupportedByEmailFeature;
  initialSelectedDraftId?: EmailTemplate["id"];
  onApply: (draftId: Draft["id"]) => void;
  onEntityOpen: EmailEntityOpenCallback;
  onHide: () => void;
}

const EmailDraftsModal = ({
  drafts,
  initialSelectedDraftId,
  onApply,
  onDraftCreate,
  onDraftDelete,
  onEntityOpen,
  onHide,
}: EmailDraftsModalProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics("More Drafts", useAnalytics());
  const deleteDraftAnalytics = useExtendedAnalytics("Delete Draft", analytics);

  const [selectedDraftId, setSelectedDraftId] = useState<EmailDraft["id"] | undefined>(
    initialSelectedDraftId
  );
  const [deletedDraftIndex, setDeletedDraftIndex] = useState<number | undefined>();

  const draftInfoGetter = useEmailDraftInfoGetter();

  const selectedDraftInfo = useMemo(() => {
    return selectedDraftId ? draftInfoGetter(selectedDraftId, intl) : undefined;
  }, [selectedDraftId, draftInfoGetter, intl]);

  useEffect(() => {
    if (!selectedDraftId) {
      return;
    }

    // refresh selected draft after drafts deleted
    if (selectedDraftId && deletedDraftIndex !== undefined) {
      const newSelectedDraftId = drafts.length
        ? drafts[deletedDraftIndex + (deletedDraftIndex === drafts.length - 1 ? -1 : 1)].id
        : undefined;
      setSelectedDraftId(newSelectedDraftId);
      setDeletedDraftIndex(undefined);
    }
  }, [drafts, selectedDraftId, deletedDraftIndex]);

  useEffect(() => {
    if (
      (selectedDraftId === undefined && drafts.length) ||
      (selectedDraftId && !drafts.find((draft) => draft.id === selectedDraftId))
    ) {
      setSelectedDraftId(drafts[0].id);
    }
  }, [drafts, selectedDraftId]);

  const handleDelete = useDynamicCallback(() => {
    if (selectedDraftInfo) {
      setDeletedDraftIndex(drafts.findIndex((draft) => draft.id === selectedDraftId));

      const notificationKey = `draft_remove_${selectedDraftInfo.draft.id}`;

      const handleUndo = () => {
        deleteDraftAnalytics.cancelled();
        return new Promise<void>((resolve, reject) => {
          onDraftCreate({
            attachments: selectedDraftInfo.draft.attachments,
            callback: () => {
              notification.close(notificationKey);
              resolve();
            },
            displayErrorNotification: true,
            draft: {
              content: selectedDraftInfo.draft.content,
              title: selectedDraftInfo.draft.title,
              type: selectedDraftInfo.draft.type,
            },
            entities: selectedDraftInfo.entities,
            entityType: selectedDraftInfo.draft.metadata.recipientType,
            failureCallback: () => {
              reject();
            },
            resources: selectedDraftInfo.resources,
          });
        });
      };

      onDraftDelete({
        callback: () => {
          notification.success({
            key: notificationKey,
            message: (
              <div className={styles.deleteMessageContainer}>
                <span>{intl.formatMessage(messages.draftDeleteSuccessMessage)}</span>
                <DraftDeleteUndoButton intl={intl} onUndo={handleUndo} />
              </div>
            ),
          });
          deleteDraftAnalytics.completed();
        },
        draft: selectedDraftInfo.draft,
        failureCallback: () => {
          notification.error({
            description: intl.formatMessage(messages.draftDeleteErrorDescription),
            message: intl.formatMessage(messages.draftDeleteErrorMessage),
          });
        },
      });

      if (drafts.length === 1) {
        onHide();
      }

      deleteDraftAnalytics.initiated();
    }
  });

  const handleApply = useDynamicCallback(() => {
    if (selectedDraftId) {
      onApply(selectedDraftId);
      analytics.completed(["Apply Draft"]);
    }
  });

  const handleEntityOpen = useDynamicCallback<EmailEntityOpenCallback>((entityId, entityType) => {
    onHide();
    onEntityOpen(entityId, entityType);
  });

  const renderDraftsList = () => {
    const now = new Date();
    return (
      <div className={styles.draftsList}>
        {drafts.map((draft) => {
          const draftInfo = draftInfoGetter(draft.id, intl);
          // draft info maybe undefined while draft is removing during re-rendering
          if (!draftInfo) {
            return null;
          }

          const draftSelected = draft.id === selectedDraftId;
          const draftSaveDate = parseApiDate(draft.updatedAt);
          const draftSaveDateFormat =
            now.getFullYear() === draftSaveDate.getFullYear()
              ? intl.formatMessage(messages.draftSaveShortDateFormat)
              : "PP";

          return (
            <div
              className={cn(styles.draftListItem, {
                [styles.selected]: draftSelected,
              })}
              key={draft.id}
              onClick={() => setSelectedDraftId(draft.id)}
            >
              <Paragraph
                className={styles.draftListItemTitle}
                ellipsis={{
                  expandable: false,
                  rows: 1,
                  tooltip: {
                    placement: "left",
                  },
                }}
              >
                {draft.title ? (
                  <EmailSubjectView
                    dynamicVariableClassName={cn(styles.draftListItemTitleDynamicVariable)}
                    dynamicVariables={draftInfo.subjectDynamicVariables}
                    subject={draft.title}
                  />
                ) : (
                  intl.formatMessage(messages.noSubject)
                )}
              </Paragraph>
              <span className={styles.draftListItemDate}>
                {formatDate(draftSaveDate, draftSaveDateFormat)}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSelectedDraft = () => {
    if (!selectedDraftInfo) {
      return null;
    }

    return (
      <div className={styles.selectedDraft}>
        <div className={styles.draftHeader}>
          <span className={styles.draftTitle}>
            {selectedDraftInfo.draft.title ? (
              <EmailSubjectView
                dynamicVariableClassName={styles.draftTitleDynamicVariable}
                dynamicVariables={selectedDraftInfo.subjectDynamicVariables}
                subject={selectedDraftInfo.draft.title}
              />
            ) : (
              intl.formatMessage(messages.noSubject)
            )}
          </span>
          <Dropdown
            menu={{
              items: [
                {
                  key: "delete",
                  label: intl.formatMessage(messages.deleteDraft),
                  onClick: handleDelete,
                },
              ],
            }}
            overlayClassName={styles.draftMenu}
            placement="bottomRight"
          >
            <MoreButton horizontal type="control" />
          </Dropdown>
        </div>
        <div className={styles.draftContent}>
          <DraftView draftInfo={selectedDraftInfo} onEntityOpen={handleEntityOpen} />
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        centered
        className={styles.modal}
        footer={<DraftApplyButton draftInfo={selectedDraftInfo} onApply={handleApply} />}
        onCancel={onHide}
        open
        scrollable
        title={
          <>
            <span>{intl.formatMessage(messages.drafts)}</span>
          </>
        }
        width="clamp(300px, 80vw, 950px)"
      >
        <div className={styles.container}>
          {renderDraftsList()}
          <Divider className={styles.columnsDivider} type="vertical" />
          {renderSelectedDraft()}
        </div>
      </Modal>
    </>
  );
};

const mapStateToProps = (state: RootState, { entityType }: EmailDraftsModalProps) => ({
  drafts: getEmailDraftsByEntityType(state, entityType),
});

const mapDispatchToProps = {
  onDraftCreate: createEmailDraft.request,
  onDraftDelete: deleteEmailDraft.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailDraftsModal);
